import React, { ReactNode, useMemo } from 'react';
import { withTheme } from '@material-ui/core/styles';

import noop from 'lodash/noop';

import { DatasetInspectorFilters } from 'Types/enums';

import SyncIcon from '@material-ui/icons/Sync';

import { DatasetInspectorContextState } from '../DatasetInspectorContext';
import {
  getIndexWithoutDeleted,
  getCompleteLog,
  getIndexWithDeleted,
} from '../../../internals/datasets';

import Button from '../../../components/common/Button/Button';
import { LinkButton } from '../../../components/common/LinkButton/LinkButton';

import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CircularProgress from '@material-ui/core/CircularProgress';
import ErrorOutline from '@material-ui/icons/ErrorOutline';

import { TestID } from '../../../testID';

import type { Dataset } from '../../../types/dataset.types';
import type { Theme } from '../../../types/common.types';

import './DatasetInspectorMessageStyle.css';

const DatasetInspectorMessage: React.FC<{
  state: DatasetInspectorContextState;
  filter: string;
  theme: Theme;
  entitiesLiveUpdated: boolean;
  isLiveUpdatesEnabled: boolean;
  customActions?: ReactNode[];
  dataset: Dataset;
  offsetDifference?: number;
  refreshEntities: () => void;
  stopSearch: () => void;
}> = ({
  state,
  dataset,
  filter,
  customActions = [],
  entitiesLiveUpdated,
  isLiveUpdatesEnabled,
  theme,
  offsetDifference,
  refreshEntities,
  stopSearch,
}) => {
  let total = 0;
  let filterFriendlyName = '';

  switch (filter) {
    case DatasetInspectorFilters.CompleteLog:
      total = getCompleteLog(dataset);
      filterFriendlyName = 'all';
      break;
    case DatasetInspectorFilters.LatestWithoutDeleted:
      total = getIndexWithoutDeleted(dataset);
      filterFriendlyName = 'latest';
      break;
    case DatasetInspectorFilters.LatestWithDeleted:
      total = getIndexWithDeleted(dataset);
      filterFriendlyName = 'latest /w deleted';
      break;
  }

  const freetextProgress = state.freetextProgress ?? 0;

  const progressPercentage = Math.round((freetextProgress * 100) / total);

  const message = useMemo(() => {
    if (state.showingFreetextResults || state.showingIdResults) {
      if (state.populating || state.loadingMore || state.loadingMoreTop) {
        return (
          <div className="dataset-inspector-message__message">
            <CircularProgress
              size={20}
              thickness={5}
              style={{ marginLeft: '2px', marginRight: '2px' }}
            />
            <div
              className="dataset-inspector-message__text"
              data-testid={TestID.SearchPipeEntitiesInProgressStatusHeader}
            >
              {`Searching...${state.showingFreetextResults ? ` (${progressPercentage}%)` : ''}`}
            </div>
          </div>
        );
      } else {
        if (state.hasMore === true || state.hasMoreTop === true) {
          return (
            <div className="dataset-inspector-message__message">
              <CheckCircleOutlineIcon color="primary" className="dataset-inspector-message__icon" />
              <div className="dataset-inspector-message__text">
                {`Search paused... (${state.entities.length} found so far)`}
              </div>
            </div>
          );
        } else {
          return (
            <div className="dataset-inspector-message__message">
              <CheckCircleOutlineIcon color="primary" className="dataset-inspector-message__icon" />
              <div
                className="dataset-inspector-message__text"
                data-testid={TestID.SearchPipeEntitiesCompletedStatusHeader}
              >
                {`Search complete (${state.entities.length} found)`}
              </div>
            </div>
          );
        }
      }
    } else {
      if (isLiveUpdatesEnabled === true && entitiesLiveUpdated === true) {
        return (
          <div className="dataset-inspector-message__message">
            <div
              className="dataset-inspector-message__text refresh-entities-link-wrapper"
              data-testid={TestID.EntitiesHasChangedMessage}
            >
              <LinkButton
                title="Entities has been changed. Click to see updated version"
                className="refresh-entities-link-wrapper"
                onClick={refreshEntities}
              >
                <SyncIcon
                  color="primary"
                  className="dataset-inspector-message__icon sync-entities"
                />{' '}
                Refresh entities {`(${offsetDifference})`}
              </LinkButton>
            </div>
          </div>
        );
      }

      if (state.isFilterDirty) {
        return (
          <div className="dataset-inspector-message__message">
            <ErrorOutline color="action" className="dataset-inspector-message__icon" />
            <div
              className="dataset-inspector-message__text"
              data-testid={TestID.SearchPipeClickGoStatusHeader}
            >
              Click GO to update results
            </div>
          </div>
        );
      } else {
        if (state.entities.length > 0) {
          return (
            <div className="dataset-inspector-message__message">
              <CheckCircleOutlineIcon color="primary" className="dataset-inspector-message__icon" />
              <div
                className="dataset-inspector-message__text"
                data-testid={TestID.EntitiesBrowsingMessage}
              >
                {`Browsing ${filterFriendlyName}`}
              </div>
            </div>
          );
        } else if (state.populating) {
          return (
            <div className="dataset-inspector-message__message">
              <CircularProgress
                size={20}
                thickness={5}
                style={{ marginLeft: '2px', marginRight: '2px' }}
              />
              <div className="dataset-inspector-message__text">Loading...</div>
            </div>
          );
        } else {
          return (
            <div className="dataset-inspector-message__message">
              <CheckCircleOutlineIcon color="primary" className="dataset-inspector-message__icon" />
              <div className="dataset-inspector-message__text">No entities found</div>
            </div>
          );
        }
      }
    }
  }, [
    filterFriendlyName,
    progressPercentage,
    state.entities.length,
    state.hasMore,
    state.hasMoreTop,
    state.isFilterDirty,
    state.loadingMore,
    state.loadingMoreTop,
    state.populating,
    state.showingFreetextResults,
    state.showingIdResults,
    entitiesLiveUpdated,
    isLiveUpdatesEnabled,
    offsetDifference,
  ]);

  return (
    <div
      className="dataset-inspector-message"
      style={{
        backgroundColor: theme.palette.background.semilight,
        color: theme.palette.text.primary,
      }}
    >
      <div>{message}</div>
      <div className="dataset-inspector-message__actions">
        {state.showingFreetextResults &&
          (state.populating || state.loadingMore || state.loadingMoreTop) && (
            <Button onClick={stopSearch ?? noop} size="small">
              Stop
            </Button>
          )}
        {customActions.length > 0 &&
          customActions.map((action: ReactNode, i: number) => (
            <div key={i} className="dataset-inspector-message__custom-action">
              {action}
            </div>
          ))}
      </div>
    </div>
  );
};

const Themed = withTheme(DatasetInspectorMessage as any);

export { Themed as DatasetInspectorMessage };
